import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaCalendarAlt, FaUsers, FaBed, FaRupeeSign, FaSearch, FaEye, FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BookingModalAdmin from '../components/BookingModalAdmin';
import BedPriceCard from '../components/BedPriceCard';
import TaxRateCard from '../components/TaxRateCard';
import { useNavigate } from 'react-router-dom';
import GuestManagement from '../components/GuestManagement';
import EmailDebugger from '../components/EmailDebugger';
import { getUnavailableBeds } from '../utils/bedAvailability';
import Pagination from '../components/Pagination';

import { getAllBookingsFromFirestore, addBookingToFirestore, updateBookingInFirestore, deleteBookingFromFirestore } from '../utils/firestoreBooking';
import { updatePaymentStatusDirect } from '../utils/directPaymentUpdate';
import { createRazorpayOrder, verifyRazorpayPayment } from '../utils/razorpayOrders';
import { migrateLocalStorageBookingsToFirestore, checkForLocalStorageBookings } from '../utils/bookingMigration';

const initialBookings = [];

// No mock stats data


const DashboardContainer = styled.div`
  background: #fafbfc;
  min-height: calc(100vh - 200px);
  padding: 2rem 1rem;
`;

const DashboardContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
`;

const StatCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #fff;
  
  &.bookings { background: #3b82f6; }
  &.checkins { background: #10b981; }
  &.beds { background: #f59e0b; }
  &.revenue { background: #8b5cf6; }
`;

const StatInfo = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #111;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
`;

const StatChange = styled.div`
  color: #10b981;
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const TabsContainer = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const TabsHeader = styled.div`
  display: flex;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const Tab = styled.button`
  padding: 1rem 1.5rem;
  border: none;
  background: transparent;
  color: #6b7280;
  font-weight: 500;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
  
  &.active {
    color: #111;
    border-bottom-color: #3b82f6;
    background: #fff;
  }
  
  &:hover {
    color: #111;
  }
`;

const TabContent = styled.div`
  padding: 1.5rem;
`;

const TableHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1rem 1.5rem;
  padding: 0.5rem 0.5rem 0 0.5rem;
  box-sizing: border-box;
  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
    padding: 0.5rem 0.2rem 0 0.2rem;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 300px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.8rem 0.8rem 0.8rem 2.5rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #232d4d;
    box-shadow: 0 0 0 3px rgba(35, 45, 77, 0.1);
  }
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
`;

const AddButton = styled.button`
  background: #232d4d;
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  max-width: 150px;

  &:hover {
  background: #1a223d;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(35, 45, 77, 0.15);
  }

  @media (max-width: 768px) {
    max-width: 120px;
    padding: 0.7rem 1rem;
    font-size: 0.9rem;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
`;

const Th = styled.th`
  background: #f8fafc;
  padding: 1.2rem 1rem;
  text-align: left;
  font-weight: 600;
  color: #334155;
  border-bottom: 1px solid #e2e8f0;
`;

const Td = styled.td`
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  color: #334155;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.2s;
  margin: 0 0.2rem;

  &:hover {
    background: #f1f5f9;
    color: #232d4d;
  }
`;

const StatusBadge = styled.span`
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  text-transform: capitalize;

  ${props => props.status === 'confirmed' && `
    background: #dcfce7;
    color: #166534;
  `}

  ${props => props.status === 'pending' && `
    background: #fef3c7;
    color: #92400e;
  `}

  ${props => props.status === 'checked-in' && `
    background: #dbeafe;
    color: #1e40af;
  `}

  ${props => props.status === 'checked-out' && `
    background: #e0e7ff;
    color: #3730a3;
  `}

  ${props => props.status === 'cancelled' && `
    background: #fee2e2;
    color: #991b1b;
  `}
`;

const PaymentBadge = styled.span`
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  text-transform: capitalize;

  ${props => props.payment === 'paid' && `
    background: #dcfce7;
    color: #166534;
  `}

  ${props => props.payment === 'pending' && `
    background: #fef3c7;
    color: #92400e;
  `}

  ${props => props.payment === 'partial' && `
    background: #dbeafe;
    color: #1e40af;
  `}

  ${props => props.payment === 'refunded' && `
    background: #fee2e2;
    color: #991b1b;
  `}
`;

// Alias for compatibility
const PaymentStatus = PaymentBadge;

const FilterButton = styled.button`
  background: white;
  color: #334155;
  border: 1px solid #ddd;
  padding: 0.8rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }

  @media (max-width: 768px) {
    padding: 0.7rem 0.8rem;
    font-size: 0.9rem;
  }
`;

const FilterDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  z-index: 100;
  min-width: 180px;
  margin-top: 0.5rem;
  overflow: hidden;
`;

const FilterOption = styled.div`
  padding: 0.8rem 1rem;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.95rem;

  &:hover {
    background: #f1f5f9;
  }

  &.active {
    background: #232d4d;
    color: white;
  }
`;


const AdminDashboardPage = () => {
  const [bookings, setBookings] = useState([]);
  
  // Shared refresh function for both tabs
  const refreshBookings = async (forceRefresh = false) => {
    try {
      console.log('Admin refreshing bookings...', forceRefresh ? '(forced)' : '');
      const firestoreBookings = await getAllBookingsFromFirestore(forceRefresh);
      console.log('Admin Firestore bookings fetched:', firestoreBookings.length);
      
      // Also check localStorage for any backup bookings
      const allLocalBookings = JSON.parse(localStorage.getItem('userBookings') || '[]');
      console.log('Admin localStorage bookings found:', allLocalBookings.length);
      
      // Merge bookings with stronger deduplication
      const bookingMap = new Map();
      
      // Add Firestore bookings first (they take priority)
      firestoreBookings.forEach(booking => {
        bookingMap.set(booking.id, { ...booking, source: 'firestore' });
      });
      
      // Add localStorage bookings, prioritizing localStorage data for existing bookings
      allLocalBookings.forEach(localBooking => {
        if (!bookingMap.has(localBooking.id)) {
          // New localStorage-only booking
          bookingMap.set(localBooking.id, {
            ...localBooking,
            // Ensure consistent data structure
            guestName: localBooking.guestName || localBooking.formData?.firstName + ' ' + localBooking.formData?.lastName || localBooking.name || 'N/A',
            guestEmail: localBooking.guestEmail || localBooking.formData?.email || localBooking.email || 'N/A',
            guestPhone: localBooking.guestPhone || localBooking.formData?.phone || localBooking.phone || 'N/A',
            checkIn: localBooking.checkIn || localBooking.bookingData?.start || 'N/A',
            checkOut: localBooking.checkOut || localBooking.bookingData?.end || 'N/A',
            total: localBooking.total || localBooking.amount || localBooking.bookingData?.total || 0,
            source: 'localStorage'
          });
        } else {
          // Booking exists in both - use localStorage data if it's more recent or has emergency fix
          const existingBooking = bookingMap.get(localBooking.id);
          const localModified = new Date(localBooking.lastModified || localBooking.updatedAt || 0);
          const firestoreModified = new Date(existingBooking.lastModified || existingBooking.updatedAt || 0);
          
          if (localBooking.emergencyFix || localModified > firestoreModified) {
            console.log(`📝 Using localStorage data for booking ${localBooking.id} (more recent or emergency fix)`);
            bookingMap.set(localBooking.id, {
              ...existingBooking,
              ...localBooking,
              source: 'localStorage-priority'
            });
          }
        }
      });
      
      const allBookings = Array.from(bookingMap.values());
      
      // Sort by creation date (newest first) - more robust sorting
      allBookings.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.bookingData?.createdAt || a.updatedAt || 0);
        const dateB = new Date(b.createdAt || b.bookingData?.createdAt || b.updatedAt || 0);
        return dateB - dateA;
      });
      
      setBookings(allBookings);
      console.log('Admin loaded bookings:', allBookings.length, 'total');
      
      // Trigger custom event for cross-tab sync
      window.dispatchEvent(new CustomEvent('bookingsUpdated', { 
        detail: { bookings: allBookings, timestamp: Date.now() } 
      }));
      
      if (allBookings.length === 0) {
        console.warn('No bookings found in either Firestore or localStorage');
      }
      
      return allBookings;
    } catch (error) {
      console.error('Error fetching bookings from Firestore:', error);
      
      // Fallback to localStorage only
      try {
        const localBookings = JSON.parse(localStorage.getItem('userBookings') || '[]');
        const processedLocalBookings = localBookings.map(booking => ({
          ...booking,
          // Ensure consistent data structure
          guestName: booking.guestName || booking.formData?.firstName + ' ' + booking.formData?.lastName || 'N/A',
          guestEmail: booking.guestEmail || booking.formData?.email || 'N/A',
          guestPhone: booking.guestPhone || booking.formData?.phone || 'N/A',
          checkIn: booking.checkIn || booking.bookingData?.start || 'N/A',
          checkOut: booking.checkOut || booking.bookingData?.end || 'N/A',
          total: booking.total || booking.amount || booking.bookingData?.total || 0,
          source: 'localStorage'
        }));
        setBookings(processedLocalBookings);
        console.log('Admin loaded from localStorage only:', processedLocalBookings.length);
        
        if (processedLocalBookings.length === 0) {
          console.warn('No bookings found in localStorage either');
        }
        return processedLocalBookings;
      } catch (localError) {
        console.error('Error loading from localStorage:', localError);
        setBookings([]);
        return [];
      }
    }
  };

  useEffect(() => {
    refreshBookings();
    
    // Automatically migrate localStorage bookings to Firebase
    const autoMigrateBookings = async () => {
      try {
        console.log('🔍 Checking for localStorage bookings that need migration...');
        const migrationCheck = await checkForLocalStorageBookings();
        console.log('📊 Migration check result:', migrationCheck);
        
        if (migrationCheck.needsMigration) {
          console.log(`🚨 Found ${migrationCheck.count} localStorage-only bookings that need migration`);
          console.log('📋 Bookings needing migration:', migrationCheck.bookings?.map(b => b.id));
          
          // Automatically start migration
          console.log('🔄 Starting automatic migration...');
          toast.info('Auto-migrating localStorage bookings to Firebase...');
          
          const result = await migrateLocalStorageBookingsToFirestore();
          
          toast.success(`Auto-migration completed! Migrated: ${result.migrated}, Skipped: ${result.skipped}, Failed: ${result.failed}`);
          console.log('✅ Auto-migration completed:', result);
          
          // Refresh bookings to show updated data
          await refreshBookings(true);
          
        } else {
          console.log('✅ No localStorage bookings need migration');
        }
      } catch (error) {
        console.error('❌ Error during auto-migration:', error);
        toast.error('Auto-migration failed. Some bookings may need manual attention.');
      }
    };
    
    autoMigrateBookings();
    
    // Set up polling to refresh bookings every 30 seconds
    const interval = setInterval(() => refreshBookings(), 30000);
    
    // Listen for cross-tab sync events
    const handleBookingsUpdated = (event) => {
      console.log('AdminDashboard: Received bookings update event');
      setBookings(event.detail.bookings);
    };
    
    window.addEventListener('bookingsUpdated', handleBookingsUpdated);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('bookingsUpdated', handleBookingsUpdated);
    };
  }, []);

  const [activeTab, setActiveTab] = useState('bookings');
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bedInventoryPosition, setBedInventoryPosition] = useState('lower');
  const [inventoryStartDate, setInventoryStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [inventoryEndDate, setInventoryEndDate] = useState(new Date(Date.now() + 24*60*60*1000).toISOString().split('T')[0]);
  const [unavailableBeds, setUnavailableBeds] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const navigate = useNavigate();

  // Load bed inventory for selected date range
  useEffect(() => {
    const loadBedInventory = async () => {
      if (!inventoryStartDate || !inventoryEndDate) return;
      
      setLoadingInventory(true);
      try {
        console.log(`Loading bed inventory for ${inventoryStartDate} to ${inventoryEndDate}`);
        const unavailable = await getUnavailableBeds(inventoryStartDate, inventoryEndDate);
        console.log('Unavailable beds:', unavailable);
        setUnavailableBeds(unavailable);
      } catch (error) {
        console.error('Error loading bed inventory:', error);
        toast.error('Failed to load bed inventory');
      } finally {
        setLoadingInventory(false);
      }
    };

    loadBedInventory();
  }, [inventoryStartDate, inventoryEndDate, bookings]); // Add bookings dependency


  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this booking? This action cannot be undone.')) {
      return;
    }

    console.log('Admin attempting to delete booking with ID:', id);
    
    let deletionSuccessful = false;

    // Delete from localStorage
    try {
      const localBookings = JSON.parse(localStorage.getItem('userBookings') || '[]');
      const initialCount = localBookings.length;
      const updatedLocalBookings = localBookings.filter(booking => booking.id !== id);
      
      if (updatedLocalBookings.length < initialCount) {
        localStorage.setItem('userBookings', JSON.stringify(updatedLocalBookings));
        console.log('Admin deleted from localStorage successfully');
        deletionSuccessful = true;
      } else {
        console.log('Admin: Booking not found in localStorage');
      }
    } catch (localError) {
      console.error('Admin error deleting from localStorage:', localError);
    }

    // Update UI immediately regardless of Firestore
    setBookings(prevBookings => {
      const filtered = prevBookings.filter(booking => booking.id !== id);
      if (filtered.length < prevBookings.length) {
        deletionSuccessful = true;
      }
      return filtered;
    });

    // Try Firestore deletion (optional)
    try {
      await deleteBookingFromFirestore(id);
      console.log('Admin deleted from Firestore successfully');
    } catch (firestoreError) {
      console.error('Admin Firestore deletion failed:', firestoreError);
      // Don't show error - localStorage deletion is sufficient
    }

    // Show result
    if (deletionSuccessful) {
      toast.success('Booking deleted successfully');
    } else {
      toast.error('Booking not found or already deleted');
    }
  };

  const handleEdit = id => {
    const booking = bookings.find(b => b.id === id);
    setSelectedBooking(booking);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleView = id => {
    const booking = bookings.find(b => b.id === id);
    setSelectedBooking(booking);
    setModalMode('view');
    setModalOpen(true);
  };

  const handleAdd = () => {
    navigate('/admin/booking-admin', { state: { mode: 'add' } });
  };


  // Quick payment status toggle function
  const handleQuickPaymentToggle = async (booking) => {
    try {
      const newPaymentStatus = booking.payment === 'paid' ? 'pending' : 'paid';
      console.log('Quick Payment Toggle: Changing', booking.id, 'from', booking.payment, 'to', newPaymentStatus);
      
      await updatePaymentStatusDirect(booking.id, newPaymentStatus);
      
      // Update local state immediately
      setBookings(prevBookings => 
        prevBookings.map(b => 
          b.id === booking.id 
            ? { ...b, payment: newPaymentStatus }
            : b
        )
      );
      
      toast.success(`Payment status changed to ${newPaymentStatus}`);
      
    } catch (error) {
      console.error('Quick Payment Toggle Error:', error);
      toast.error('Failed to update payment status');
    }
  };

  const handleSave = async (data) => {
    try {
      console.log('AdminDashboardPage: handleSave called with mode:', modalMode);
      console.log('AdminDashboardPage: Data received:', data);
      console.log('AdminDashboardPage: Selected booking ID:', selectedBooking?.id);
      
      if (modalMode === 'add') {
        const bookingData = {
          ...data,
          id: `SKY${Date.now()}`,
          createdAt: new Date().toISOString(),
          status: 'Confirmed',
          payment: 'paid'
        };
        
        await addBookingToFirestore(bookingData);
        toast.success('Booking added successfully');
        
        // Refresh bookings
        const updatedBookings = await getAllBookingsFromFirestore();
        setBookings(updatedBookings);
      } else if (modalMode === 'edit' && selectedBooking) {
        console.log('AdminDashboardPage: Saving booking edit...');
        console.log('AdminDashboardPage: Selected booking ID:', selectedBooking.id);
        console.log('AdminDashboardPage: Payment status in data:', data.payment);
        
        // Check if payment status changed
        const paymentChanged = data.payment !== selectedBooking.payment;
        console.log('AdminDashboardPage: Payment status changed?', paymentChanged, 'From:', selectedBooking.payment, 'To:', data.payment);
        
        if (paymentChanged) {
          // Use direct payment update for payment status changes
          console.log('AdminDashboardPage: Using DIRECT payment update method');
          await updatePaymentStatusDirect(selectedBooking.id, data.payment);
          
          // Update other fields separately if needed
          const otherData = { ...data };
          delete otherData.payment; // Remove payment from other updates
          
          if (Object.keys(otherData).length > 0) {
            await updateBookingInFirestore(selectedBooking.id, otherData);
          }
        } else {
          // Normal update for non-payment changes
          await updateBookingInFirestore(selectedBooking.id, data);
        }
        
        // Update local state immediately
        setBookings(prevBookings => 
          prevBookings.map(booking => 
            booking.id === selectedBooking.id 
              ? { ...booking, ...data }
              : booking
          )
        );
        
        toast.success('Booking updated successfully');
        
        // Force refresh after a short delay
        setTimeout(async () => {
          await refreshBookings(true);
        }, 1500);
      }
    } catch (error) {
      console.error('Error saving booking:', error);
      console.error('Error details:', error.message);
      toast.error('Failed to save booking');
    }
    setModalOpen(false);
  };

  const handleGuestAction = async (bookingId, action) => {
    try {
      const updateData = {};
      if (action === 'check-in') {
        updateData.status = 'checked-in';
        updateData.checkInTime = new Date().toISOString();
      } else if (action === 'check-out') {
        updateData.status = 'checked-out';
        updateData.checkOutTime = new Date().toISOString();
      }
      
      await updateBookingInFirestore(bookingId, updateData);
      toast.success(`Guest ${action} action completed successfully`);
      
      // Refresh bookings
      const updatedBookings = await getAllBookingsFromFirestore();
      setBookings(updatedBookings);
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error('Failed to update booking status');
    }
  };

  // Apply search filter (bookings are already sorted newest first)
  const filteredBookings = bookings.filter(booking => {
    // Guard against undefined/null fields to prevent runtime errors
    const idStr = (booking.id || '').toString().toLowerCase();
    const nameStr = (booking.name || booking.guestName || '').toLowerCase();
    const emailStr = (booking.email || booking.guestEmail || '').toLowerCase();
    const phoneStr = (booking.phone || booking.guestPhone || booking.formData?.phone || '').toLowerCase();
    const bedTypeStr = (booking.bedType || '').toLowerCase();
    const statusStr = (booking.status || '').toLowerCase();
    const paymentStr = (booking.payment || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    
    return (
      idStr.includes(search) ||
      nameStr.includes(search) ||
      emailStr.includes(search) ||
      phoneStr.includes(search) ||
      bedTypeStr.includes(search) ||
      statusStr.includes(search) ||
      paymentStr.includes(search)
    );
  });

  // Calculate pagination
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedBookings = filteredBookings.slice(startIndex, endIndex);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // --- Dynamic Stats ---
  const TOTAL_BEDS = 38; // Room A (22) + Room B (16) = 38 total beds
  const today = new Date();
  const todayStr = today.toISOString().slice(0,10);
  const month = today.getMonth();
  const year = today.getFullYear();

  const totalBookings = bookings.length;
  const todayCheckIns = bookings.filter(b => {
    if (!b.checkIn) return false;
    // Accept both string and Date
    const checkInDate = new Date(b.checkIn);
    return checkInDate.toISOString().slice(0,10) === todayStr && b.status !== 'cancelled';
  }).length;

  // Calculate beds currently booked (overlapping today, not cancelled)
  const bedsBookedToday = bookings.filter(b => {
    if (!b.checkIn || !b.checkOut) return false;
    const inDate = new Date(b.checkIn);
    const outDate = new Date(b.checkOut);
    return inDate <= today && outDate > today && b.status !== 'cancelled';
  }).reduce((sum, b) => sum + (parseInt(b.beds,10)||0), 0);
  const availableBeds = Math.max(0, TOTAL_BEDS - bedsBookedToday);

  // Calculate monthly revenue (bookings with checkIn or checkOut in current month, not cancelled)
  const monthlyRevenue = bookings.filter(b => {
    if (!b.checkIn || !b.checkOut) return false;
    const inDate = new Date(b.checkIn);
    const outDate = new Date(b.checkOut);
    return (inDate.getMonth() === month && inDate.getFullYear() === year ||
            outDate.getMonth() === month && outDate.getFullYear() === year) &&
           b.status !== 'cancelled';
  }).reduce((sum, b) => sum + (parseFloat(b.amount)||0), 0);

  const renderGuestManagement = () => {
    return <GuestManagement />;
  };


  // Test payment function for ₹1 to verify payment flow
  const handleTestPayment = async () => {
    try {
      console.log('🧪 Starting ₹1 test payment...');
      
      // Load Razorpay script
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => handleTestPayment();
        document.body.appendChild(script);
        return;
      }

      // Create test order for ₹1
      const order = await createRazorpayOrder(1, 'INR', `test_${Date.now()}`);
      console.log('✅ Test order created:', order);

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        // Only use order_id for real orders, not mock orders
        ...(order.is_mock ? {} : { order_id: order.id }),
        amount: order.amount, // ₹1 in paise (100)
        currency: order.currency,
        name: 'Skyline Backpackers - TEST',
        description: '₹1 Test Payment - Admin Testing',
        handler: async function (response) {
          console.log('✅ Test payment successful:', response);
          console.log('Response keys:', Object.keys(response));
          console.log('Response values:', response);
          
          // Check what fields are actually available in the response
          const hasPaymentId = response.razorpay_payment_id || response.payment_id;
          
          if (!hasPaymentId) {
            console.log('❌ No payment ID found in response');
            console.log('Available fields:', Object.keys(response));
            toast.error('Payment failed - no payment ID received', {
              position: "top-right",
              autoClose: 5000,
            });
            return;
          }
          
          // For test payments, having payment_id is sufficient to confirm success
          console.log('✅ Payment ID received:', hasPaymentId);
          toast.success('✅ Test payment successful! Payment flow is working correctly.', {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
        },
        prefill: {
          name: 'Admin Test User',
          email: 'admin@skylinebackpackers.in',
          contact: '9032797797'
        },
        theme: {
          color: '#3399cc'
        },
        modal: {
          ondismiss: function() {
            console.log('Test payment modal dismissed');
            toast.info('Test payment cancelled', {
              position: "top-right",
              autoClose: 3000,
            });
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      
    } catch (error) {
      console.error('❌ Test payment failed:', error);
      toast.error(`Test payment failed: ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  return (
    <>
      <Header />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '1rem 0 2rem 0', maxWidth: 1200, padding: '0 1rem', marginLeft: 'auto', marginRight: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 20, color: '#181818' }}>Admin Dashboard</span>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button 
            onClick={handleTestPayment}
            style={{ 
              background: '#10b981', 
              color: 'white', 
              border: 'none', 
              borderRadius: 6, 
              padding: '6px 12px', 
              fontWeight: 600, 
              fontSize: 14, 
              cursor: 'pointer',
              display: 'flex', 
              alignItems: 'center', 
              gap: 6 
            }}
          >
            <FaRupeeSign />
            Test Payment (₹1)
          </button>
          <button 
            onClick={() => navigate('/admin/invoice')}
            style={{ 
              background: '#232d4d', 
              color: 'white', 
              border: 'none', 
              borderRadius: 6, 
              padding: '6px 12px', 
              fontWeight: 600, 
              fontSize: 14, 
              cursor: 'pointer',
              display: 'flex', 
              alignItems: 'center', 
              gap: 6 
            }}
          >
            <FaRupeeSign /> Generate Invoice
          </button>
          <span style={{ background: '#f3f4f6', color: '#232d4d', borderRadius: 6, padding: '6px 18px', fontWeight: 600, fontSize: 14, boxShadow: '0 1px 4px #e0e7ef' }}>Administrator</span>
        </div>
      </div>
      <DashboardContainer>
        <DashboardContent>
          {/* Removed fixed bed price card - pricing now dynamic per package */}
          {/* Stats Cards */}
          <StatsGrid>
            <StatCard>
              <StatIcon className="bookings">
                <FaCalendarAlt />
              </StatIcon>
              <StatInfo>
                <StatValue>{totalBookings}</StatValue>
                <StatLabel>Total Bookings</StatLabel>
                <StatChange></StatChange>
              </StatInfo>
            </StatCard>

            <StatCard>
              <StatIcon className="checkins">
                <FaUsers />
              </StatIcon>
              <StatInfo>
                <StatValue>{todayCheckIns}</StatValue>
                <StatLabel>Today's Check-ins</StatLabel>
                <StatChange></StatChange>
              </StatInfo>
            </StatCard>

            <StatCard>
              <StatIcon className="beds">
                <FaBed />
              </StatIcon>
              <StatInfo>
                <StatValue>{availableBeds}</StatValue>
                <StatLabel>Available Beds</StatLabel>
                <StatChange>Out of 38 total beds</StatChange>
              </StatInfo>
            </StatCard>

            <StatCard>
              <StatIcon className="revenue">
                <FaRupeeSign />
              </StatIcon>
              <StatInfo>
                <StatValue>₹{monthlyRevenue.toLocaleString()}</StatValue>
                <StatLabel>Monthly Revenue</StatLabel>
                <StatChange></StatChange>
              </StatInfo>
            </StatCard>
          </StatsGrid>

          {/* Tabs Container */}
          <TabsContainer>
            <TabsHeader>
              <Tab 
                className={activeTab === 'bookings' ? 'active' : ''} 
                onClick={() => setActiveTab('bookings')}
              >
                Bookings
              </Tab>
              <Tab 
                className={activeTab === 'inventory' ? 'active' : ''} 
                onClick={() => setActiveTab('inventory')}
              >
                Bed Inventory
              </Tab>
              <Tab 
                className={activeTab === 'guests' ? 'active' : ''} 
                onClick={() => setActiveTab('guests')}
              >
                Guest Management
              </Tab>
              <Tab 
                className={activeTab === 'email-debug' ? 'active' : ''} 
                onClick={() => setActiveTab('email-debug')}
              >
                Email Debug
              </Tab>
            </TabsHeader>

            <TabContent>
              {activeTab === 'bookings' && (
                <>
                  <TableHeader>
                    <h3 style={{ margin: 0, color: '#111' }}>Recent Bookings</h3>
                    <div style={{ display: 'flex', gap: '2.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <SearchContainer>
                        <SearchIcon />
                        <SearchInput
                          type="text"
                          placeholder="Search bookings..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </SearchContainer>
                      <AddButton style={{ marginLeft: 24, minWidth: 140 }} onClick={handleAdd} type="button">
                        <FaPlus /> Add Booking
                      </AddButton>
                    </div>
                  </TableHeader>

                  <div style={{overflowX: 'auto', width: '100%'}}>
                    <Table style={{minWidth: 700}}>
                      <thead>
                      <tr>
                        <Th>S.No</Th>
                        <Th>Booking ID</Th>
                        <Th>Guest</Th>
                        <Th>Dates</Th>
                        <Th>Bed Details</Th>
                        <Th>Amount</Th>
                        <Th>Status</Th>
                        <Th>Payment</Th>
                        <Th>Actions</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedBookings.length === 0 ? (
                        <tr>
                          <Td colSpan="9" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                            {filteredBookings.length === 0 ? 'No bookings found.' : 'No bookings on this page.'}
                          </Td>
                        </tr>
                      ) : (
                        paginatedBookings.map((booking, index) => (
                          <tr key={`${booking.id}-${booking.source || 'unknown'}-${index}`}>
                            <Td style={{ textAlign: 'center', fontWeight: '600', color: '#6b7280' }}>{startIndex + index + 1}</Td>
                            <Td style={{ textAlign: 'left', fontWeight: '600' }}>{booking.id}</Td>
                            <Td style={{ textAlign: 'left', fontWeight: '500' }}>
                              <div>{booking.guestName || booking.name || 'N/A'}</div>
                              <div style={{ color: '#6b7280', fontSize: '0.85em' }}>{booking.guestEmail || 'N/A'}</div>
                              <div style={{ color: '#6b7280', fontSize: '0.85em' }}>{booking.guestPhone || 'N/A'}</div>
                            </Td>
                            <Td style={{ textAlign: 'left', fontSize: '0.95rem' }}>
  {booking.checkIn}
  <div style={{ color: '#6b7280', fontSize: '0.85em' }}>to {booking.checkOut}</div>
</Td>
                            <Td style={{ textAlign: 'left' }}>
  <div>{booking.bedType}</div>
  <div style={{ color: '#6b7280', fontSize: '0.85em' }}>{booking.beds} bed{booking.beds > 1 ? 's' : ''}</div>
</Td>
                            <Td style={{ fontWeight: '600' }}>₹{booking.total || booking.amount || 0}</Td>
                            <Td>
                              <StatusBadge className={booking.status}>
                                {booking.status}
                              </StatusBadge>
                            </Td>
                            <Td>
                              <PaymentBadge 
                          payment={booking.payment}
                          onClick={() => handleQuickPaymentToggle(booking)}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          title="Click to toggle payment status"
                        >
                          {booking.payment || 'pending'}
                        </PaymentBadge>
                            </Td>
                            <Td>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
  <ActionButton onClick={() => navigate('/admin/booking-admin', { state: { mode: 'view', booking } })} title="View Booking">
    <FaEye />
  </ActionButton>
  <ActionButton onClick={() => navigate('/admin/booking-admin', { state: { mode: 'edit', booking } })} title="Edit Booking">
    <FaEdit />
  </ActionButton>
  <ActionButton onClick={() => {
    if(window.confirm('Are you sure you want to delete this booking?')) handleDelete(booking.id);
  }} title="Delete Booking" style={{ color: '#b91c1c' }}>
    <FaTrash />
  </ActionButton>
</div>
                            </Td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                  </div>

                  {/* Pagination Component */}
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={itemsPerPage}
                    totalItems={filteredBookings.length}
                  />
                </>
              )}

              {activeTab === 'inventory' && (
                <div style={{ padding: '2.5rem 0' }}>
                  <h2 style={{ color: '#232d4d', marginBottom: 16, textAlign: 'center' }}>Bed Inventory</h2>
                  
                  {/* Date Range Selector */}
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <label style={{ fontWeight: '600', color: '#232d4d' }}>From:</label>
                      <input
                        type="date"
                        value={inventoryStartDate}
                        onChange={(e) => setInventoryStartDate(e.target.value)}
                        style={{
                          padding: '0.5rem',
                          border: '1px solid #e0e0e0',
                          borderRadius: '6px',
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <label style={{ fontWeight: '600', color: '#232d4d' }}>To:</label>
                      <input
                        type="date"
                        value={inventoryEndDate}
                        onChange={(e) => setInventoryEndDate(e.target.value)}
                        style={{
                          padding: '0.5rem',
                          border: '1px solid #e0e0e0',
                          borderRadius: '6px',
                          fontSize: '0.9rem'
                        }}
                      />
                    </div>
                    {loadingInventory && (
                      <div style={{ color: '#666', fontSize: '0.9rem' }}>Loading inventory...</div>
                    )}
                  </div>
                  
                  {/* Upper/Lower Toggle */}
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
                    <button
                      style={{
                        background: bedInventoryPosition === 'lower' ? '#232d4d' : '#f5f7fa',
                        color: bedInventoryPosition === 'lower' ? '#fff' : '#232d4d',
                        border: '1.5px solid #e0e0e0',
                        fontWeight: 600,
                        fontSize: '1rem',
                        borderRadius: 22,
                        padding: '0.45rem 1.3rem',
                        marginRight: 8,
                        cursor: 'pointer',
                        outline: 'none',
                        boxShadow: 'none',
                        transition: 'all 0.18s cubic-bezier(.4,0,.2,1)'
                      }}
                      onClick={() => setBedInventoryPosition('lower')}
                    >Lower Beds</button>
                    <button
                      style={{
                        background: bedInventoryPosition === 'upper' ? '#232d4d' : '#f5f7fa',
                        color: bedInventoryPosition === 'upper' ? '#fff' : '#232d4d',
                        border: '1.5px solid #e0e0e0',
                        fontWeight: 600,
                        fontSize: '1rem',
                        borderRadius: 22,
                        padding: '0.45rem 1.3rem',
                        cursor: 'pointer',
                        outline: 'none',
                        boxShadow: 'none',
                        transition: 'all 0.18s cubic-bezier(.4,0,.2,1)'
                      }}
                      onClick={() => setBedInventoryPosition('upper')}
                    >Upper Beds</button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, justifyContent: 'center' }}>
                    {[{ room: 1, beds: 22 }, { room: 2, beds: 16 }].map(({ room, beds }) => {
                      // Bed layout with upper/lower logic
                      const bedPrefix = room === 1 ? 'A' : 'B';
                      const totalBeds = beds;
                      const rowsNeeded = room === 1 ? 6 : 4;
                      let bedNum = 1;
                      // Build grid: rows x 4 cols
                      const grid = [];
                      for (let row = 0; row < rowsNeeded; row++) {
                        const rowBeds = [];
                        for (let col = 0; col < 4; col++) {
                          if (bedNum <= totalBeds) {
                            const pos = bedNum % 2 === 1 ? 'lower' : 'upper';
                            if (pos === bedInventoryPosition) {
                              rowBeds.push({
                                id: `${bedPrefix}${bedNum}`,
                                display: `${bedPrefix}${bedNum}`,
                                position: pos
                              });
                            } else {
                              rowBeds.push(null);
                            }
                            bedNum++;
                          } else {
                            rowBeds.push(null);
                          }
                        }
                        grid.push(rowBeds);
                      }
                      // Get real bed statuses based on unavailable beds
                      const statuses = Array.from({ length: beds }, (_, i) => {
                        const bedId = `${bedPrefix}${i + 1}`;
                        return unavailableBeds.includes(bedId) ? 'booked' : 'available';
                      });
                      const counts = {
                        available: statuses.filter(s => s === 'available').length,
                        booked: statuses.filter(s => s === 'booked').length,
                      };
                      return (
                        <div key={room} style={{ background: '#fff', borderRadius: 14, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: 24, minWidth: 320, maxWidth: 420, flex: 1 }}>
                          <h3 style={{ color: '#1e40af', marginBottom: 12 }}>Room {room} <span style={{ fontSize: 15, color: '#64748b' }}>({beds} beds)</span></h3>
                          <div style={{ display: 'grid', gridTemplateRows: `repeat(${rowsNeeded}, 1fr)`, gridTemplateColumns: `repeat(4, 1fr)`, gap: 10, marginBottom: 18 }}>
                            {grid.flat().map((bed, idx) => bed ? (
                              <div key={bed.display} style={{
                                borderRadius: 8,
                                background: statuses[bed.id.slice(1) - 1] === 'available' ? '#e0f2fe' : '#fee2e2',
                                color: statuses[bed.id.slice(1) - 1] === 'available' ? '#0284c7' : '#b91c1c',
                                fontWeight: 600,
                                fontSize: 15,
                                textAlign: 'center',
                                padding: '12px 0',
                                border: '1.5px solid #e5e7eb',
                                boxShadow: statuses[bed.id.slice(1) - 1] === 'booked' ? '0 1px 4px #fee2e2' : '0 1px 4px #e0f2fe',
                                transition: 'background 0.2s',
                              }}>
                                {bed.display}
                                <div style={{ fontSize: 11, fontWeight: 500, marginTop: 2 }}>{statuses[bed.id.slice(1) - 1].charAt(0).toUpperCase() + statuses[bed.id.slice(1) - 1].slice(1)}</div>
                                <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>{bed.position} bed</div>
                              </div>
                            ) : <div key={`empty-${idx}`}></div>)}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, marginBottom: 8 }}>
                            <span><b>Total:</b> {beds}</span>
                            <span style={{ color: '#0284c7' }}><b>Available:</b> {counts.available}</span>
                            <span style={{ color: '#b91c1c' }}><b>Booked:</b> {counts.booked}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: 32, textAlign: 'center' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ width: 22, height: 22, background: '#e0f2fe', borderRadius: 6, display: 'inline-block', border: '1.5px solid #bae6fd' }}></span> Available
                      <span style={{ width: 22, height: 22, background: '#fee2e2', borderRadius: 6, display: 'inline-block', border: '1.5px solid #fecaca', marginLeft: 18 }}></span> Booked
                    </span>
                  </div>
                </div>
              )}

              {activeTab === 'guests' && (
                renderGuestManagement()
              )}

              {activeTab === 'email-debug' && (
                <EmailDebugger />
              )}
            </TabContent>
          </TabsContainer>
        </DashboardContent>
      </DashboardContainer>
      <Footer />
      <BookingModalAdmin
        open={modalOpen}
        mode={modalMode}
        booking={selectedBooking}
        onSave={handleSave}
        onClose={() => setModalOpen(false)}
      />
      <ToastContainer position="bottom-center" autoClose={2000} />
    </>
  );
};

export default AdminDashboardPage;
